import { Helmet } from "react-helmet-async";
import { useEffect, useState } from "react";
import {
  Card,
  Stack,
  Container,
  Typography,
  CircularProgress,
  Grid,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  Snackbar,
} from "@mui/material";
import FixtureCard from "src/components/FixtureCard";
import { get, post } from "src/utils/networkUtils";
import UsersCountSummary from "src/components/users/UsersCountSummary";


export default function ScoringPage() {
  const [loading, setLoading] = useState(false);
  const [stageStats, setStageStats] = useState(null);
  const [stages, setStages] = useState([]);
  const [stageId, setStageId] = useState('');
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [reverting, setReverting] = useState(false);
  const [alert, setAlert] = useState({ open: false, severity: 'success', message: '' });

  // Extract tenantId from URL query parameters, default to 1 if not present
  const searchParams = new URLSearchParams(window.location.search);
  const tenantId = searchParams.get('tenantId') || '1';

  useEffect(() => {
    get(`/admin/stages?tenantId=${tenantId}`).then(function (response) {
      setStages(response.stages);
      // find ongoing stage
      const ongoingStage = response.stages.find(
        (stage) =>
          stage.start * 1000 <= Date.now() && stage.end * 1000 >= Date.now()
      );
      if (ongoingStage) {
        setStageId(ongoingStage.stageId);
      } else if (response.stages.length > 0) {
        setStageId(response.stages[0].stageId);
      }
    });


  }, [tenantId]);

  useEffect(() => {
    if (!stageId) return;
    setLoading(true);
    get(`/admin/stages/${stageId}/scoring-stats?tenantId=${tenantId}`).then(function (response) {
      setLoading(false);
      setStageStats(response);
    });
  }, [stageId, tenantId]);

  const handleRevertScoring = () => {
    setConfirmDialogOpen(false);
    setReverting(true);
    // Call API to revert scoring
    post(`/admin/stages/${stageId}/revert-scoring`).then((response) => {
      setReverting(false);
      setAlert({
        open: true,
        severity: 'success',
        message: 'Scoring reverted successfully!'
      });
      // Reload the stats
      if (stageId) {
        setLoading(true);
        get(`/admin/stages/${stageId}/scoring-stats?tenantId=${tenantId}`).then(function (response) {
          setLoading(false);
          setStageStats(response);
        });
      }
    }).catch((error) => {
      setReverting(false);
      setAlert({
        open: true,
        severity: 'error',
        message: 'Failed to revert scoring. Please try again.'
      });
    });
  };

  const handleOpenConfirmDialog = () => {
    setConfirmDialogOpen(true);
  };

  const handleCloseConfirmDialog = () => {
    setConfirmDialogOpen(false);
  };

  const handleCloseAlert = () => {
    setAlert({ ...alert, open: false });
  };

  const reloadScoringStats = () => {
    if (stageId) {
      get(`/admin/stages/${stageId}/scoring-stats?tenantId=${tenantId}`).then(function (response) {
        setStageStats(response);
      });
    }
  };

  console.log("stageStats", stageStats);
  return (
    <>
      <Helmet>
        <title>Scoring</title>
      </Helmet>

      <Container>
        <Snackbar
          open={alert.open}
          autoHideDuration={6000}
          onClose={handleCloseAlert}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert onClose={handleCloseAlert} severity={alert.severity} sx={{ width: '100%' }}>
            {alert.message}
          </Alert>
        </Snackbar>

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={5}
        >
          <Typography variant="h4" gutterBottom>
            Scoring
          </Typography>
          <Button
            variant="contained"
            onClick={handleOpenConfirmDialog}
            disabled={reverting}
            startIcon={reverting ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {reverting ? 'Reverting...' : 'Revert Scoring'}
          </Button>
        </Stack>
        <Box sx={{ p: 2 }}>
          <FormControl fullWidth>
            <InputLabel id="status-select-label">Stage</InputLabel>
            <Select
              labelId="stage-label"
              id="stage-select"
              value={stageId}
              label="Stage"
              onChange={(event) => setStageId(event.target.value)}
            >
              {stages !== null &&
                stages.map((row, index) => (
                  <MenuItem key={row.stageId} value={row.stageId}>{row.name}</MenuItem>
                ))}
            </Select>
          </FormControl>
        </Box>

        {loading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '400px',
            }}
          >
            <CircularProgress size={60} />
          </Box>
        ) : (
          <>
            <Card>
              <Grid container spacing={2} sx={{ p: 2 }}>
                {stageStats && stageStats.fixtures.map((fixture) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={fixture.fixtureId}>
                    <FixtureCard fixture={fixture} onScoringComplete={reloadScoringStats} tenantId={tenantId} />
                  </Grid>
                ))}
              </Grid>
            </Card>
            {stageStats && (
              <Grid container spacing={2} sx={{ my: 2 }}>
                <Grid item xs={3}>
                  <UsersCountSummary
                    title="Lineups"
                    total={stageStats.lineupStats?.TotalLineups ?? 0}
                  />
                </Grid>
                <Grid item xs={3}>
                  <UsersCountSummary
                    title="Slots"
                    total={stageStats.lineupStats?.TotalSlots ?? 0}
                  />
                </Grid>
                <Grid item xs={3}>
                  <UsersCountSummary
                    title="Scored Slots"
                    total={stageStats.lineupStats?.ScoredSlots ?? 0}
                  />
                </Grid>
                <Grid item xs={3}>
                  <UsersCountSummary
                    title="Unscored Slots"
                    total={stageStats.lineupStats?.ScoredSlots ? stageStats.lineupStats.TotalSlots - stageStats.lineupStats.ScoredSlots : 0}
                  />
                </Grid>
              </Grid>
            )}
            <Grid container spacing={2} sx={{ mt: 2 }}>
              <Grid item xs={3}>
                <Card>
                  <Typography variant="h6" sx={{ p: 2 }}>
                    Top Lineups
                  </Typography>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>ID</TableCell>
                          <TableCell>Username</TableCell>
                          <TableCell align="right">Score</TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {stageStats && stageStats.top10LineupsWithMostPoints && stageStats.top10LineupsWithMostPoints.map((lineup) => (
                          <TableRow key={lineup.LineupId}>
                            <TableCell>{lineup.LineupId}</TableCell>
                            <TableCell>{lineup.Username}</TableCell>
                            <TableCell align="right">{lineup.Score}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Card>
              </Grid>
              <Grid item xs={3}>
                <Card>
                  <Typography variant="h6" sx={{ p: 2 }}>
                    Top Slots
                  </Typography>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>ID</TableCell>
                          <TableCell>Name</TableCell>
                          <TableCell align="right">Score</TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {stageStats && stageStats.top10SlotsWithMostPoints && stageStats.top10SlotsWithMostPoints.map((lineup) => (
                          <TableRow key={lineup.LineupSlotId}>
                            <TableCell>{lineup.LineupSlotId}</TableCell>
                            <TableCell>{lineup.Firstname} {lineup.Lastname}</TableCell>
                            <TableCell align="right">{lineup.Score}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Card>
              </Grid>
              <Grid item xs={3}>
                <Card>
                  <Typography variant="h6" sx={{ p: 2 }}>
                    Uncored Slots
                  </Typography>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>ID</TableCell>
                          <TableCell>Name</TableCell>
                          <TableCell align="right">Score</TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {stageStats && stageStats.top10UnscoredSlots && stageStats.top10UnscoredSlots.map((lineup) => (
                          <TableRow key={lineup.LineupSlotId}>
                            <TableCell>{lineup.LineupSlotId}</TableCell>
                            <TableCell>{lineup.Firstname} {lineup.Lastname}</TableCell>
                            <TableCell align="right">{lineup.Score}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Card>
              </Grid>
              <Grid item xs={3}>
                <Card>
                  <Typography variant="h6" sx={{ p: 2 }}>
                    Leaderboard
                  </Typography>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>ID</TableCell>
                          <TableCell>Name</TableCell>
                          <TableCell align="right">Score</TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {stageStats && stageStats.leaderboard && stageStats.leaderboard.map((lineup) => (
                          <TableRow key={lineup.userId}>
                            <TableCell>{lineup.userId}</TableCell>
                            <TableCell>{lineup.name}</TableCell>
                            <TableCell align="right">{lineup.score}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Card>
              </Grid>
            </Grid>
          </>
        )}

      </Container>

      <Dialog
        open={confirmDialogOpen}
        onClose={handleCloseConfirmDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          Confirm Revert Scoring
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to revert scoring? This will set all scores to zero for all users in this stage. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={handleRevertScoring} color="error" variant="contained" autoFocus>
            Revert Scoring
          </Button>
        </DialogActions>
      </Dialog>

    </>
  );
}
